import { useEffect, useMemo, useState } from 'react';
import { Box, IconButton, Typography, useTheme } from '@mui/material';
import { MdOutlineRemoveRedEye } from 'react-icons/md';
import { GoPlus } from 'react-icons/go';
import { ColumnDef } from '@tanstack/react-table';
import { useForm } from 'react-hook-form';
import dayjs from 'dayjs';
import DataTable from 'components/Common/DataTable';
import Pagination, { usePagination } from 'components/Common/Pagination';
import Button from 'components/Common/Button';
import Search from '@/components/Common/Search';
import ChurnSignalFilterSelect from './ChurnSignalFilterSelect';
import { useDestroyTagging, useGetTaggedRecords, useGetTaggingContexts } from '@/apis/agency/taggings/hook';
import { Tag, TaggedRecord } from '@/apis/agency/taggings/types';
import { useOpenBusinessWorkspace } from '../hooks/useOpenBusinessWorkspace';
import BusinessTagPill from './BusinessTagPill';
import RemoveChurnSignalModal from './RemoveChurnSignalModal';
import AddChurnSignalsDrawer from './AddChurnSignalsDrawer';
import { buildChurnSignalSelectOptions } from './businessTags';

interface FilterFormValues {
  signal_types: string[];
}

export default function ChurnRiskBusinessesSection() {
  const theme = useTheme();
  const { currentPage, perPage, setPage, setPerPage, resetPage } = usePagination();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [tagToRemove, setTagToRemove] = useState<Tag | null>(null);
  const [searchText, setSearchText] = useState<string | null>(null);
  const { openBusinessWorkspaceById } = useOpenBusinessWorkspace();

  const { control, watch } = useForm<FilterFormValues>({
    defaultValues: { signal_types: [] }
  });

  const selectedSignalTypes = watch('signal_types');

  const { data: contexts, isLoading: contextsLoading } = useGetTaggingContexts();

  const signalFilterOptions = useMemo(() => buildChurnSignalSelectOptions(contexts), [contexts]);

  useEffect(() => {
    resetPage();
  }, [selectedSignalTypes, searchText, resetPage]);

  const queryParams = useMemo(() => {
    const filter: Record<string, unknown> = {
      context_eq: 'churn_signal',
      taggable_type: 'Business'
    };

    if (selectedSignalTypes.length > 0) {
      filter.name_eq = selectedSignalTypes;
    }

    if (searchText?.trim()) {
      filter.name_cont = searchText.trim();
    }

    return {
      page: currentPage,
      per_page: perPage,
      filter
    };
  }, [currentPage, perPage, selectedSignalTypes, searchText]);

  const { data, isLoading } = useGetTaggedRecords(queryParams);

  const { mutate: destroyTag, isPending: isResolving } = useDestroyTagging(() => {
    setTagToRemove(null);
  });

  const columns: ColumnDef<TaggedRecord>[] = useMemo(
    () => [
      {
        header: 'Business',
        accessorKey: 'name',
        cell: (info) => (
          <Box>
            <Typography>{info.row.original.name}</Typography>
            <Typography fontSize={12} color='text.secondary'>
              {info.row.original.uid}
            </Typography>
          </Box>
        )
      },
      {
        header: 'Billing Start Date',
        accessorKey: 'plan_started_at',
        size: 10,
        cell: (info) => {
          const date = info.getValue() as string | null;
          if (!date) return '-';
          return dayjs(date).format('MMM DD, YYYY');
        }
      },
      {
        header: 'Churn Signals',
        accessorKey: 'tags',
        cell: (info) => (
          <Box display='flex' flexWrap='wrap' alignItems='center'>
            {(info.row.original.tags ?? [])
              .filter((tag) => tag.context === 'churn_signal')
              .map((tag) => (
                <BusinessTagPill key={tag.id} tag={tag} onRemove={setTagToRemove} />
              ))}
          </Box>
        )
      },
      {
        header: 'Action',
        size: 10,
        cell: (info) => (
          <IconButton
            size='small'
            sx={{
              padding: { xs: '4px', sm: '8px' },
              '& svg': {
                fontSize: { xs: '18px', sm: '24px' }
              }
            }}
            onClick={() => openBusinessWorkspaceById(info.row.original.id, { openInNewTab: true })}
          >
            <MdOutlineRemoveRedEye size={24} color='#777777' />
          </IconButton>
        )
      }
    ],
    [openBusinessWorkspaceById]
  );

  return (
    <Box bgcolor='#fff' border='1px solid #ECECEC' borderRadius={2} p={2} mt={3}>
      <Box display='flex' justifyContent='space-between' alignItems='flex-start' flexWrap='wrap' gap={2} mb={2}>
        <Box>
          <Typography variant='h6' fontWeight={500}>
            Active Churn Risk Businesses
          </Typography>
          <Typography color='text.secondary' fontSize={14}>
            Businesses flagged by system-identified churn signals.
          </Typography>
        </Box>

        <Box display='flex' alignItems='center' gap={1.5} flexWrap='wrap'>
          <Box minWidth={200} width={{ xs: '100%', sm: 220 }}>
            <Search
              placeholder='Search business'
              searchText={searchText}
              setSearchText={setSearchText}
              size='small'
              fullWidth
            />
          </Box>

          <Box minWidth={200} width={{ xs: '100%', sm: 220 }}>
            <ChurnSignalFilterSelect
              name='signal_types'
              control={control}
              label='Select Signals'
              options={signalFilterOptions}
              isLoading={contextsLoading}
            />
          </Box>

          <Button
            variant='outlined'
            startIcon={<GoPlus />}
            onClick={() => setDrawerOpen(true)}
            sx={{ borderColor: theme.palette.primary.main, color: theme.palette.primary.main }}
          >
            Add Signals to Business
          </Button>
        </Box>
      </Box>

      <DataTable data={data?.data} columns={columns} isHeaderSticky isLoading={isLoading} />
      <Pagination
        totalPages={data?.meta?.total_pages}
        totalCount={data?.meta?.total_count ?? 0}
        currentPage={currentPage}
        perPage={perPage}
        onChange={setPage}
        onPerPageChange={setPerPage}
      />

      <AddChurnSignalsDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />

      <RemoveChurnSignalModal
        open={Boolean(tagToRemove)}
        tag={tagToRemove}
        onClose={() => setTagToRemove(null)}
        onConfirm={() => {
          if (tagToRemove && !isResolving) {
            destroyTag(tagToRemove.id);
          }
        }}
      />
    </Box>
  );
}
